import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupSubmenuListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Stack of open submenu IDs for back navigation
   * @type {string[]}
   */
  #menuStack = [];

  /**
   * Scroll position when drawer opened (to restore on close)
   * @type {number}
   */
  #scrollPosition = 0;

  /**
   * Setup event listeners for submenu triggers and back buttons
   */
  #setupSubmenuListeners() {
    // Submenu triggers
    const submenuTriggers = this.querySelectorAll('[data-submenu-trigger]');
    submenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.#onSubmenuTriggerClick);
    });

    // Back buttons
    const backButtons = this.querySelectorAll('.menu-drawer__back-button');
    backButtons.forEach(button => {
      button.addEventListener('click', this.#onBackButtonClick);
    });
  }

  /**
   * Lock body scroll to prevent background scrolling
   */
  #lockBodyScroll() {
    this.#scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollPosition}px`;
    document.body.style.width = '100%';
    document.body.style.touchAction = 'none';
    document.body.classList.add('drawer-open');
  }

  /**
   * Unlock body scroll and restore position
   */
  #unlockBodyScroll() {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.touchAction = '';
    document.body.classList.remove('drawer-open');
    window.scrollTo(0, this.#scrollPosition);
  }

  /**
   * Handle submenu trigger click
   * @param {MouseEvent} event
   */
  #onSubmenuTriggerClick = (event) => {
    event.preventDefault();
    const trigger = event.currentTarget;
    const submenuId = trigger.getAttribute('data-submenu-trigger');
    if (submenuId) {
      this.openSubmenu(submenuId);
    }
  };

  /**
   * Handle back button click
   * @param {MouseEvent} event
   */
  #onBackButtonClick = (event) => {
    event.preventDefault();
    this.closeSubmenu();
  };

  /**
   * Open a submenu by ID
   * @param {string} submenuId
   */
  openSubmenu(submenuId) {
    const submenu = this.querySelector(`#${submenuId}`);
    if (!submenu) return;

    this.#menuStack.push(submenuId);
    submenu.classList.add('menu-drawer__submenu--open');
    
    // Update trigger aria-expanded
    const trigger = this.querySelector(`[data-submenu-trigger="${submenuId}"]`);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    // Focus management - focus on the back button
    setTimeout(() => {
      const backButton = submenu.querySelector('.menu-drawer__back-button');
      if (backButton) {
        backButton.focus();
      }
    }, 100);
  }

  /**
   * Close the current submenu (back navigation)
   */
  closeSubmenu() {
    if (this.#menuStack.length === 0) return;

    const submenuId = this.#menuStack.pop();
    const submenu = this.querySelector(`#${submenuId}`);
    if (submenu) {
      submenu.classList.remove('menu-drawer__submenu--open');
    }

    // Update trigger aria-expanded
    const trigger = this.querySelector(`[data-submenu-trigger="${submenuId}"]`);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      // Return focus to trigger
      setTimeout(() => {
        trigger.focus();
      }, 100);
    }
  }

  /**
   * Close all open submenus
   */
  closeAllSubmenus() {
    while (this.#menuStack.length > 0) {
      this.closeSubmenu();
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    this.#lockBodyScroll();
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.closeAllSubmenus();
    this.#close(this.refs.details);
  }

  /**
   * Go back one level or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    // If we have open submenus, go back one level
    if (this.#menuStack.length > 0) {
      this.closeSubmenu();
      return;
    }
    // Otherwise close the main drawer
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.closeAllSubmenus();
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        this.#unlockBodyScroll();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
